import { Head } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { creatorListingsPath, serviceCreatorStagePath } from "@travily/routes";
import type ListingsCreatorPhotos from "@travily/types/pages/ListingsCreatorPhotos";
import { SERVICE_PHOTO_PATHS } from "@/hooks/listings/photo-paths";
import { usePhotoManager } from "@/hooks/listings/use-photo-manager";
import { useCreatorTopBarActions } from "@/lib/wizard/use-creator-top-bar-actions";
import { useWizardForm } from "@/lib/wizard/use-wizard-form";
import { ServicePhotosStepView } from "./show.view";

type PageProps = ListingsCreatorPhotos;

type FormData = {
  photos: string;
};

const ServicePhotosStep = ({ listing_id, stage_index, stage_order, photos, min_photos }: PageProps) => {
  const { fieldError, currentStage, totalStages, onBack, onPrimary, primaryPending } = useWizardForm<FormData>({
    routes: {
      stageUrl: (stage) => serviceCreatorStagePath(listing_id, stage),
      entryUrl: creatorListingsPath(),
    },
    stageName: "photos",
    stageIndex: stage_index,
    stageOrder: stage_order,
    initialData: { photos: "" },
  });
  const manager = usePhotoManager({ initialPhotos: photos, listingId: listing_id, paths: SERVICE_PHOTO_PATHS });

  const photosError = fieldError("photos");

  const topBarActions = useCreatorTopBarActions({ saveHref: creatorListingsPath() });

  return (
    <>
      <Head title={t("web.services.creator.steps.photos.show.meta_title")}>
        <meta name="description" content={t("web.services.creator.steps.photos.show.meta_description")} />
      </Head>
      <ServicePhotosStepView
        currentStage={currentStage}
        totalStages={totalStages}
        onBack={onBack}
        topBarActions={topBarActions}
        onPrimary={onPrimary}
        primaryPending={primaryPending}
        photos={manager.photos}
        minPhotos={min_photos}
        photosError={photosError}
        onAddMore={manager.openModal}
        onDeletePhoto={manager.requestDelete}
        onReorder={manager.reorderPhotos}
        uploadModalOpen={manager.isModalOpen}
        uploadFiles={manager.upload.files}
        uploadIsUploading={manager.upload.isUploading}
        uploadAllCompleted={manager.upload.allCompleted}
        onUploadAddFiles={manager.upload.addFiles}
        onUploadConfirm={manager.confirmUpload}
        onUploadModalOpenChange={(open) => {
          if (!open) manager.closeModal();
        }}
        onUploadRemoveFile={manager.upload.removeFile}
        deleteModalOpen={manager.deleteTarget !== null}
        onDeleteConfirm={manager.confirmDelete}
        onDeleteModalOpenChange={(open) => {
          if (!open) manager.cancelDelete();
        }}
      />
    </>
  );
};

export default ServicePhotosStep;
